import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import adminApi from "../api/adminAxios";

const inr = n => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n);

const STATUS_COLORS = {
  pending:    { color: "#c47a3a", bg: "#fff4ec" },
  processing: { color: "#c4a020", bg: "#fffbec" },
  shipped:    { color: "#3a6ab0", bg: "#eef4ff" },
  delivered:  { color: "#3a8a68", bg: "#eefaf4" },
  cancelled:  { color: "#c0392b", bg: "#fff0f0" },
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminApi.get("admin/stats/")
      .then(r => setStats(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const adminUser = JSON.parse(localStorage.getItem("admin_user") || "{}");

  const STAT_CARDS = stats ? [
    { label: "Total Revenue", value: inr(stats.total_revenue), icon: "💰", color: "#3a8a68" },
    { label: "Total Orders", value: stats.total_orders, icon: "📦", color: "#3a6ab0" },
    { label: "Active Customers", value: stats.total_users, icon: "👥", color: "#c47a3a" },
    { label: "Products Listed", value: stats.total_products, icon: "🕯️", color: "#8a3a8a" },
  ] : [];

  return (
    <div style={{ padding: "32px 36px" }}>
      {/* Header */}
      <div style={{ marginBottom: "28px" }}>
        <h1 style={{ fontFamily: "Georgia, serif", fontSize: "24px", fontWeight: "700", color: "#1a1a1a" }}>Dashboard Overview</h1>
        <p style={{ fontFamily: "Georgia, serif", fontSize: "13px", color: "#aaa", marginTop: "4px" }}>
          Welcome back, {adminUser.username}. Here's what's happening today.
        </p>
      </div>

      {loading ? (
        <p style={{ fontFamily: "Georgia, serif", color: "#aaa", fontSize: "13px" }}>Loading...</p>
      ) : (
        <>
          {/* Stat cards */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "16px", marginBottom: "28px" }}>
            {STAT_CARDS.map(({ label, value, icon, color }) => (
              <div key={label} style={{ background: "#fff", borderRadius: "14px", padding: "20px", border: "1px solid #f0ebe3", boxShadow: "0 2px 8px rgba(0,0,0,.04)" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "14px" }}>
                  <div style={{ width: "40px", height: "40px", borderRadius: "10px", background: `${color}15`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "20px" }}>{icon}</div>
                </div>
                <p style={{ fontFamily: "Georgia, serif", fontSize: "22px", fontWeight: "700", color: "#1a1a1a", marginBottom: "4px" }}>{value}</p>
                <p style={{ fontFamily: "Georgia, serif", fontSize: "11px", color: "#aaa" }}>{label}</p>
              </div>
            ))}
          </div>

          {/* Order status summary */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "14px", marginBottom: "28px" }}>
            {[
              { label: "Pending Orders", value: stats?.pending_orders, color: "#c47a3a", bg: "#fff4ec" },
              { label: "In Transit", value: stats?.shipped_orders, color: "#3a6ab0", bg: "#eef4ff" },
              { label: "Delivered", value: stats?.delivered_orders, color: "#3a8a68", bg: "#eefaf4" },
            ].map(({ label, value, color, bg }) => (
              <div key={label} style={{ background: bg, borderRadius: "12px", padding: "16px 20px", display: "flex", alignItems: "center", gap: "14px" }}>
                <div style={{ width: "10px", height: "10px", borderRadius: "50%", background: color, flexShrink: 0 }} />
                <div>
                  <p style={{ fontFamily: "Georgia, serif", fontSize: "20px", fontWeight: "700", color }}>{value ?? 0}</p>
                  <p style={{ fontFamily: "Georgia, serif", fontSize: "11px", color: "#888" }}>{label}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Recent Orders */}
          <div style={{ background: "#fff", borderRadius: "16px", padding: "24px", border: "1px solid #f0ebe3" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
              <div>
                <h2 style={{ fontFamily: "Georgia, serif", fontSize: "16px", fontWeight: "700", color: "#1a1a1a" }}>Recent Orders</h2>
                <p style={{ fontFamily: "Georgia, serif", fontSize: "11px", color: "#aaa", marginTop: "2px" }}>Latest customer purchases</p>
              </div>
              <button onClick={() => navigate("/admin/orders")}
                style={{ background: "none", border: "none", cursor: "pointer", fontFamily: "Georgia, serif", fontSize: "12px", color: "#e07b39", fontWeight: "700" }}>
                View All →
              </button>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "80px 1fr 1fr 100px 110px 90px", gap: "8px", padding: "8px 12px", background: "#f7f2eb", borderRadius: "8px", marginBottom: "6px" }}>
              {["ORDER ID", "CUSTOMER", "PRODUCT", "AMOUNT", "STATUS", "DATE"].map(h => (
                <span key={h} style={{ fontFamily: "Georgia, serif", fontSize: "9px", fontWeight: "700", color: "#aaa", letterSpacing: "0.08em" }}>{h}</span>
              ))}
            </div>

            {(stats?.recent_orders || []).length === 0 ? (
              <p style={{ fontFamily: "Georgia, serif", fontSize: "13px", color: "#aaa", textAlign: "center", padding: "24px 0" }}>No orders yet.</p>
            ) : (
              (stats?.recent_orders || []).map(order => {
                const cfg = STATUS_COLORS[order.status] || { color: "#888", bg: "#f5f5f5" };
                const firstItem = order.items?.[0];
                return (
                  <div key={order.id} style={{ display: "grid", gridTemplateColumns: "80px 1fr 1fr 100px 110px 90px", gap: "8px", padding: "12px 12px", borderBottom: "1px solid #f7f2eb", alignItems: "center" }}>
                    <span style={{ fontFamily: "Georgia, serif", fontSize: "12px", fontWeight: "700", color: "#e07b39" }}>#{order.id}</span>
                    <span style={{ fontFamily: "Georgia, serif", fontSize: "12px", color: "#1a1a1a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {order.user || "—"}
                    </span>
                    <span style={{ fontFamily: "Georgia, serif", fontSize: "11px", color: "#666", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {firstItem ? `${firstItem.product_name} (${firstItem.quantity})` : "—"}
                    </span>
                    <span style={{ fontFamily: "Georgia, serif", fontSize: "12px", fontWeight: "700", color: "#1a1a1a" }}>{inr(order.total_price)}</span>
                    <span style={{ padding: "3px 10px", borderRadius: "999px", fontSize: "10px", fontWeight: "700", fontFamily: "Georgia, serif", color: cfg.color, background: cfg.bg, display: "inline-block", textTransform: "capitalize" }}>
                      {order.status}
                    </span>
                    <span style={{ fontFamily: "Georgia, serif", fontSize: "11px", color: "#aaa" }}>
                      {new Date(order.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
                    </span>
                  </div>
                );
              })
            )}
          </div>
        </>
      )}
    </div>
  );
}